import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminApi, useIsBootstrapMode } from '@/hooks/useAdminApi';
import toast from 'react-hot-toast';
import {
  Github,
  Cloud,
  Check,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import { clsx } from 'clsx';

const integrationTypes = [
  {
    type: 'github',
    name: 'GitHub',
    description: 'Connect to GitHub for support tickets and knowledge base',
    icon: Github,
    category: 'general',
    fields: [
      { key: 'owner', label: 'Owner/Organization', type: 'text', required: true },
      { key: 'repo', label: 'Repository', type: 'text', required: true },
      { key: 'apiToken', label: 'Personal Access Token', type: 'password', required: true },
      { key: 'knowledgeBasePath', label: 'Knowledge Base Path', type: 'text', default: 'docs' },
      { key: 'defaultLabels', label: 'Default Labels (comma-separated)', type: 'text', default: 'portal-feedback' },
    ],
  },
  {
    type: 'jira',
    name: 'Atlassian Jira',
    description: 'Sync items with Jira issues',
    icon: Cloud,
    category: 'general',
    fields: [
      { key: 'baseUrl', label: 'Jira URL', type: 'url', required: true },
      { key: 'email', label: 'Email', type: 'email', required: true },
      { key: 'apiToken', label: 'API Token', type: 'password', required: true },
      { key: 'projectKey', label: 'Project Key', type: 'text', required: true },
    ],
  },
  {
    type: 'anthropic',
    name: 'Anthropic Claude',
    description: 'Enable AI-powered features using Claude',
    icon: Cloud,
    category: 'general',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'model', label: 'Model', type: 'text', default: 'claude-3-sonnet-20240229' },
    ],
  },
  {
    type: 'openai',
    name: 'OpenAI',
    description: 'Enable AI-powered features using OpenAI',
    icon: Cloud,
    category: 'general',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'model', label: 'Model', type: 'text', default: 'gpt-4' },
    ],
  },
];

export function AdminIntegrations() {
  const queryClient = useQueryClient();
  const apiClient = useAdminApi();
  const isBootstrapMode = useIsBootstrapMode();
  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<Record<string, any>>({});

  const { data: integrationsData } = useQuery({
    queryKey: ['integrations', isBootstrapMode],
    queryFn: () => apiClient.getIntegrations(),
  });

  // Ensure integrations is always an array (defensive check)
  const integrations = Array.isArray(integrationsData) ? integrationsData : [];

  const updateMutation = useMutation({
    mutationFn: ({ type, config }: { type: string; config: Record<string, any> }) =>
      apiClient.updateIntegration(type, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('Integration updated');
      setEditingConfig({});
    },
    onError: () => {
      toast.error('Failed to update integration');
    },
  });

  const testMutation = useMutation({
    mutationFn: (type: string) => apiClient.testIntegration(type),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message || 'Connection successful');
      } else {
        toast.error(result.message || 'Connection failed');
      }
    },
    onError: () => {
      toast.error('Failed to test connection');
    },
  });

  const getIntegrationStatus = (type: string) => {
    return integrations.find((i) => i.type === type);
  };

  const handleToggle = (type: string) => {
    if (expandedType === type) {
      setExpandedType(null);
      setEditingConfig({});
    } else {
      const existing = getIntegrationStatus(type);
      setExpandedType(type);
      setEditingConfig(existing?.config || {});
    }
  };

  const handleSave = (type: string) => {
    updateMutation.mutate({ type, config: editingConfig });
  };

  const generalIntegrations = integrationTypes.filter((i) => i.category === 'general');

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Integrations</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Connect external services to extend functionality
        </p>
      </div>

      {/* Integrations Section */}
      <div className="space-y-4">
        {generalIntegrations.map((integration) => {
          const status = getIntegrationStatus(integration.type);
          const isExpanded = expandedType === integration.type;
          const Icon = integration.icon;

          return (
            <div
              key={integration.type}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <button
                onClick={() => handleToggle(integration.type)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <Icon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {integration.name}
                      </span>
                      {status?.is_configured && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                          <Check className="h-3 w-3 mr-1" />
                          Configured
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {integration.description}
                    </p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {isExpanded && (
                <div className="px-6 pb-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="pt-4 space-y-4">
                    {integration.fields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                          type={field.type}
                          value={editingConfig[field.key] || ''}
                          onChange={(e) =>
                            setEditingConfig({ ...editingConfig, [field.key]: e.target.value })
                          }
                          placeholder={field.default || ''}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex justify-end gap-3">
                    {status?.is_configured && (
                      <button
                        onClick={() => testMutation.mutate(integration.type)}
                        disabled={testMutation.isPending}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <RefreshCw
                          className={clsx('h-4 w-4 mr-2', testMutation.isPending && 'animate-spin')}
                        />
                        Test Connection
                      </button>
                    )}
                    <button
                      onClick={() => handleSave(integration.type)}
                      disabled={updateMutation.isPending}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                    >
                      {updateMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
